import React, { useState, useEffect } from 'react';
import { useCreateVehicleMutation, useUpdateVehicleMutation, type Vehicle } from '../../store/api/referencesApi';
import type { VehicleCategoryCode } from '../../types';
import { normalizeDecimalString, toNumberOrUndefined } from '../../utils/numberUtils';
import { VEHICLE_CATEGORY_OPTIONS, vehicleCategoryLabel } from '../../constants/vehicleCategory';
import EntityDocumentsBlock from '../documents/EntityDocumentsBlock';

interface CreateVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVehicleCreated?: (vehicle: Vehicle) => void;
  vehicle?: Vehicle | null;
  /** Для новой записи: сразу открыть форму как тягач или прицеп. */
  initialVehicleCategory?: VehicleCategoryCode;
}

type FormState = {
  vehicleCategory: VehicleCategoryCode;
  registrationNumber: string;
  brand: string;
  wheelFormula: string;
  trailerType: string;
  carryingCapacity: string;
  dimensionsText: string;
  isCompanyVehicle: boolean;
};

const emptyForm = (): FormState => ({
  vehicleCategory: 'TRUCK',
  registrationNumber: '',
  brand: '',
  wheelFormula: '',
  trailerType: '',
  carryingCapacity: '',
  dimensionsText: '',
  isCompanyVehicle: false
});

const CreateVehicleModal: React.FC<CreateVehicleModalProps> = ({
  isOpen,
  onClose,
  onVehicleCreated,
  vehicle = null,
  initialVehicleCategory = 'TRUCK'
}) => {
  const [formData, setFormData] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createVehicle, { isLoading: isCreating }] = useCreateVehicleMutation();
  const [updateVehicle, { isLoading: isUpdating }] = useUpdateVehicleMutation();

  const isLoading = isCreating || isUpdating;
  const isEditMode = !!vehicle;

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.registrationNumber.trim()) {
      newErrors.registrationNumber = 'Госномер обязателен';
    }
    // Прицеп: обязательны только госномер и выбор категории TRAILER в поле «Тип ТС»; остальное — опционально.
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /** Тело запроса под справочник ТС (null явно снимает поля прицепа с тягача и наоборот). */
  const buildVehicleBody = ():
    | (Partial<Vehicle> & {
        vehicleCategory: VehicleCategoryCode;
        vehicleType: string | null;
        wheelFormula: string | null;
        carryingCapacity: number | null;
        volumeM3: number | null;
        dimensionsText: string | null;
      })
    | Record<string, unknown> => {
    const cat = formData.vehicleCategory;
    const common = {
      registrationNumber: formData.registrationNumber.trim(),
      vehicleCategory: cat,
      brand: formData.brand.trim() || null,
      isCompanyVehicle: formData.isCompanyVehicle
    };

    if (cat === 'TRUCK') {
      return {
        ...common,
        vehicleType: null,
        wheelFormula: formData.wheelFormula.trim() || null,
        carryingCapacity: null,
        volumeM3: null,
        dimensionsText: null
      };
    }

    return {
      ...common,
      vehicleType: formData.trailerType.trim() ? formData.trailerType.trim() : null,
      wheelFormula: null,
      carryingCapacity: toNumberOrUndefined(formData.carryingCapacity) ?? null,
      volumeM3: null,
      dimensionsText: formData.dimensionsText.trim() || null
    };
  };

  /** Без HTML &lt;form&gt; — модалка может открываться внутри Ant Design Form (маршрут, водитель). */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    const body = buildVehicleBody();

    try {
      if (isEditMode && vehicle) {
        const updatedVehicle = await updateVehicle({
          id: vehicle.id,
          data: body as Parameters<typeof updateVehicle>[0]['data']
        }).unwrap();
        onVehicleCreated?.(updatedVehicle as unknown as Vehicle);
      } else {
        const newVehicle = await createVehicle(body as Parameters<typeof createVehicle>[0]).unwrap();
        onVehicleCreated?.(newVehicle as unknown as Vehicle);
      }
      onClose();
      setFormData(emptyForm());
      setErrors({});
    } catch (error: unknown) {
      const msg = (error as { data?: { message?: string } })?.data?.message;
      setErrors({ general: msg || 'Ошибка при сохранении ТС' });
    }
  };

  useEffect(() => {
    if (isOpen && vehicle) {
      const cat: VehicleCategoryCode = vehicle.vehicleCategory === 'TRAILER' ? 'TRAILER' : 'TRUCK';
      setFormData({
        vehicleCategory: cat,
        registrationNumber: vehicle.registrationNumber,
        brand: vehicle.brand || '',
        wheelFormula: vehicle.wheelFormula || '',
        trailerType: cat === 'TRAILER' ? vehicle.vehicleType || '' : '',
        carryingCapacity: vehicle.carryingCapacity != null ? String(vehicle.carryingCapacity) : '',
        dimensionsText: vehicle.dimensionsText || '',
        isCompanyVehicle: !!vehicle.isCompanyVehicle
      });
    } else if (isOpen && !vehicle) {
      setFormData({ ...emptyForm(), vehicleCategory: initialVehicleCategory });
    }
    setErrors({});
  }, [isOpen, vehicle, initialVehicleCategory]);

  if (!isOpen) return null;

  const isTruck = formData.vehicleCategory === 'TRUCK';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {isEditMode ? 'Редактировать транспортное средство' : 'Создать транспортное средство'}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Тип ТС *</label>
            <select
              value={formData.vehicleCategory}
              onChange={(e) => {
                const v = e.target.value as VehicleCategoryCode;
                setField('vehicleCategory', v);
                if (v === 'TRUCK') {
                  setField('trailerType', '');
                  setField('carryingCapacity', '');
                  setField('dimensionsText', '');
                } else {
                  setField('wheelFormula', '');
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {VEHICLE_CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Тягач и прицеп вносятся отдельными записями; связь между ними не хранится.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Госномер *</label>
            <input
              type="text"
              value={formData.registrationNumber}
              onChange={(e) => setField('registrationNumber', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.registrationNumber ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="А123ВС 77"
            />
            {errors.registrationNumber && <p className="text-red-500 text-sm mt-1">{errors.registrationNumber}</p>}
          </div>

          {isTruck ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Марка</label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) => setField('brand', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Mercedes-Benz, Scania…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Колёсная формула</label>
                <input
                  type="text"
                  value={formData.wheelFormula}
                  onChange={(e) => setField('wheelFormula', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="4×2, 6×4…"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Тип прицепа</label>
                <input
                  type="text"
                  value={formData.trailerType}
                  onChange={(e) => setField('trailerType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Рефрижератор, борт, тент…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Марка</label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) => setField('brand', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Грузоподъёмность (кг)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.carryingCapacity}
                  onChange={(e) => setField('carryingCapacity', normalizeDecimalString(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="20000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Размеры</label>
                <input
                  type="text"
                  value={formData.dimensionsText}
                  onChange={(e) => setField('dimensionsText', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="13,6 × 2,55 м (необязательно)"
                />
              </div>
            </>
          )}

          {isEditMode && vehicle ? (
            <div className="border border-gray-200 rounded-md p-3">
              <div className="text-sm font-medium text-gray-700 mb-2">Документы</div>
              <EntityDocumentsBlock
                entityType="vehicle"
                entityId={vehicle.id}
                canModify
                documentDetails={{
                  VEHICLE_REGISTRATION: { number: formData.registrationNumber }
                }}
              />
            </div>
          ) : (
            <div className="border border-gray-200 rounded-md p-3 text-sm text-gray-500">
              Документы можно загрузить после создания ТС.
            </div>
          )}

          {errors.general && <div className="text-red-500 text-sm">{errors.general}</div>}

          <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-100">
            <label className="flex items-center mr-auto select-none cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isCompanyVehicle}
                onChange={(e) => setField('isCompanyVehicle', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm">ТС компании ({vehicleCategoryLabel(formData.vehicleCategory)})</span>
            </label>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Отмена
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={() => void handleSubmit()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? (isEditMode ? 'Сохранение…' : 'Создание…') : isEditMode ? 'Сохранить' : 'Создать'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateVehicleModal;
